import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import AttachmentFileIcon from 'ext-ui/svg/icon/AttachmentFileIcon'
import CheckCircleFilledIcon from 'ext-ui/svg/icon/CheckCircleFilledIcon'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import CloseIcon from 'ext-ui/svg/icon/CloseIcon'
import XCircleFilledIcon from 'ext-ui/svg/icon/XCircleFilledIcon'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Gmail2026 from 'src/assets/svgs/Gmail2026.svg'
import ReceiptEvidenceCollapse from 'src/assets/svgs/receiptEvidenceCollapse.svg'
import ReceiptEvidenceDelete from 'src/assets/svgs/receiptEvidenceDelete.svg'
import ReceiptEvidenceExternalLink from 'src/assets/svgs/receiptEvidenceExternalLink.svg'
import ReceiptEvidencePlus from 'src/assets/svgs/receiptEvidencePlus.svg'
import ReceiptAgentAvatar from 'src/components/ReceiptAgentAvatar/ReceiptAgentAvatar'
import SvgDownload from 'src/components/Svgs/SvgDownload'
import { useTranslate } from 'src/localization/Trns'
import {
  AttachmentUploadType,
  ReceiptAttachmentModel,
} from 'src/models/receiptAttachment.models'
import {
  ReceiptRecoveryCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import style from './ReceiptEvidencePanel.style'
import {
  getReviewedReceiptAttachmentId,
  hasManualAttachmentProvenance,
  isReceiptEvidenceReviewCase,
} from './receiptRecoveryPreview'

export interface ReceiptEvidenceSummary {
  submittedAt?: string
  submittedByDisplayName?: string
}

export interface ReceiptEvidence {
  receipt?: ReceiptEvidenceSummary
}

interface ReceiptEvidencePanelProps {
  receiptRecoveryCase?: ReceiptRecoveryCase
  receiptAttachment: ReceiptAttachmentModel
  receiptEvidence?: ReceiptEvidence
  onDelete?: (attachmentId: string) => void
  canDelete?: boolean
  onSubmitReview?: (outcome: 'MATCHES' | 'DOES_NOT_MATCH') => void
  isSubmittingReview?: boolean
  reviewError?: string
}

const MIN_ZOOM_LEVEL = 1
const MAX_ZOOM_LEVEL = 3
const ZOOM_LEVEL_STEP = 0.5

interface ReceiptEvidencePanState {
  pointerId: number
  clientX: number
  clientY: number
  scrollLeft: number
  scrollTop: number
}

type ReceiptPdfPreview =
  | {
      url: string
      kind: 'document' | 'image'
    }
  | null
  | undefined

const hasFileSignature = (bytes: Uint8Array, signature: number[]): boolean =>
  signature.every((byte, index) => bytes[index] === byte)

const inferReceiptPreview = async (
  previewBlob: Blob,
  responseContentType: string | null,
): Promise<{ kind: 'document' | 'image'; contentType: string } | undefined> => {
  const contentType = responseContentType?.split(';', 1)[0] || previewBlob.type

  if (contentType === 'application/pdf') {
    return { kind: 'document', contentType }
  }
  if (contentType.startsWith('image/')) {
    return { kind: 'image', contentType }
  }

  const bytes = new Uint8Array(await previewBlob.arrayBuffer()).slice(0, 12)
  if (hasFileSignature(bytes, [37, 80, 68, 70, 45])) {
    return { kind: 'document', contentType: 'application/pdf' }
  }
  if (hasFileSignature(bytes, [137, 80, 78, 71, 13, 10, 26, 10])) {
    return { kind: 'image', contentType: 'image/png' }
  }
  if (hasFileSignature(bytes, [255, 216, 255])) {
    return { kind: 'image', contentType: 'image/jpeg' }
  }

  return undefined
}

const formatDate = (value?: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return undefined

  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`
}

const filenameFromAttachment = (
  attachment: ReceiptAttachmentModel,
): string | undefined => {
  try {
    const url = new URL(attachment.urls.original)
    const filename = url.pathname.split('/').filter(Boolean).pop()
    const decodedFilename = filename ? decodeURIComponent(filename) : undefined
    return decodedFilename?.match(/\.[a-zA-Z0-9]+$/)
      ? decodedFilename
      : undefined
  } catch {
    return undefined
  }
}

type ReceiptEvidenceActionIconName =
  | 'external-link'
  | 'delete'
  | 'download'
  | 'plus'
  | 'collapse'

const ReceiptEvidenceActionIcon: React.FC<{
  name: ReceiptEvidenceActionIconName
  src?: string
}> = ({ name, src }) => (
  <span
    className={`ReceiptEvidencePanel--action-icon ReceiptEvidencePanel--action-icon-${name}`}
    data-testid={'receipt-evidence-action-icon'}
    aria-hidden={'true'}
  >
    {name === 'download' ? (
      <SvgDownload fill={'currentColor'} viewBox={'0 0 24 24'} />
    ) : (
      <img src={src} alt={''} />
    )}
  </span>
)

const ReceiptEvidencePanel: React.FC<ReceiptEvidencePanelProps> = ({
  receiptRecoveryCase,
  receiptAttachment,
  receiptEvidence,
  onDelete,
  canDelete = true,
  onSubmitReview,
  isSubmittingReview = false,
  reviewError,
}): React.ReactElement => {
  const translate = useTranslate()
  const previewRef = useRef<HTMLDivElement>(null)
  const zoomFocusRef = useRef({ x: 0.5, y: 0.5 })
  const panStateRef = useRef<ReceiptEvidencePanState>()
  const [zoomLevel, setZoomLevel] = useState(MIN_ZOOM_LEVEL)
  const [isPanning, setIsPanning] = useState(false)
  const [pendingReviewOutcome, setPendingReviewOutcome] = useState<
    'MATCHES' | 'DOES_NOT_MATCH'
  >()
  const fileName =
    receiptRecoveryCase?.recoveredFileName ??
    filenameFromAttachment(receiptAttachment) ??
    translate('Receipt')
  const originalUrl = receiptAttachment.urls.original
  const attachmentId = receiptAttachment.id
  const isDeleteEnabled =
    canDelete && attachmentId !== undefined && onDelete !== undefined
  const isImage = receiptAttachment.contentType?.startsWith('image/')
  const isPdf = receiptAttachment.contentType === 'application/pdf'
  const attachmentPreviewUrl = receiptAttachment.urls.main ?? originalUrl
  const [pdfPreview, setPdfPreview] = useState<ReceiptPdfPreview>(undefined)
  const previewUrl = isPdf ? pdfPreview?.url : attachmentPreviewUrl
  const isPreviewImage = isImage || pdfPreview?.kind === 'image'
  const isPreviewDocument = pdfPreview?.kind === 'document'
  const openUrl = isPdf
    ? (originalUrl ?? attachmentPreviewUrl)
    : attachmentPreviewUrl
  const receiptSummary = receiptEvidence?.receipt
  const isManualAttachment =
    hasManualAttachmentProvenance(receiptRecoveryCase) &&
    receiptRecoveryCase?.receiptAttachmentId === attachmentId
  const submittedAt = formatDate(
    receiptSummary?.submittedAt ??
      receiptRecoveryCase?.attachedAt ??
      receiptRecoveryCase?.lastAgentRunAt ??
      receiptAttachment.createdAt,
  )
  const submittedBy =
    !isManualAttachment && receiptRecoveryCase?.recoveredFrom
      ? translate('Receipt Agent')
      : (receiptSummary?.submittedByDisplayName ??
        receiptRecoveryCase?.attachedByDisplayName ??
        receiptAttachment.submittedByDisplayName)
  const isNeedsReview =
    receiptRecoveryCase?.lifecycleState ===
    ReceiptRecoveryLifecycleState.NeedsReview
  const reviewerDecision =
    receiptRecoveryCase?.reviewerDecision ??
    (receiptRecoveryCase?.status === 'REVIEW_REJECTED'
      ? 'REJECT'
      : receiptRecoveryCase?.lifecycleState ===
            ReceiptRecoveryLifecycleState.ReceiptReconciled &&
          receiptRecoveryCase.reconciledByDisplayName
        ? 'APPROVE'
        : undefined)
  const isReviewerDrivenReceipt =
    !isManualAttachment &&
    isReceiptEvidenceReviewCase(receiptRecoveryCase) &&
    (receiptRecoveryCase?.reviewedReceiptAttachmentId === undefined ||
      receiptRecoveryCase.reviewedReceiptAttachmentId === null ||
      attachmentId === getReviewedReceiptAttachmentId(receiptRecoveryCase))
  const reviewedBy =
    receiptRecoveryCase?.reviewedByDisplayName ??
    receiptRecoveryCase?.reconciledByDisplayName
  const isInAppUpload =
    receiptAttachment.uploadType === AttachmentUploadType.APP
  const isRecoveredFromGmail = receiptRecoveryCase?.recoveredFrom === 'GMAIL'

  useEffect(() => {
    if (!isSubmittingReview) setPendingReviewOutcome(undefined)
  }, [isSubmittingReview])

  useEffect(() => {
    if (!isPdf || !attachmentPreviewUrl) {
      setPdfPreview(undefined)
      return
    }

    const controller = new AbortController()
    let isDisposed = false
    let objectUrl: string | undefined

    const loadPreview = async (): Promise<void> => {
      try {
        const response = await fetch('/api/receipt-preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: attachmentPreviewUrl }),
          signal: controller.signal,
        })
        if (!response.ok) throw new Error('Unable to load receipt preview')

        const previewBlob = await response.blob()
        const preview = await inferReceiptPreview(
          previewBlob,
          response.headers.get('content-type'),
        )

        if (!preview) throw new Error('Unsupported receipt preview')

        objectUrl = URL.createObjectURL(
          new Blob([previewBlob], { type: preview.contentType }),
        )
        if (isDisposed) {
          URL.revokeObjectURL(objectUrl)
          return
        }

        setPdfPreview({ url: objectUrl, kind: preview.kind })
      } catch (error) {
        if (!isDisposed && (error as DOMException).name !== 'AbortError') {
          setPdfPreview(null)
        }
      }
    }

    setPdfPreview(undefined)
    void loadPreview()

    return () => {
      isDisposed = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachmentPreviewUrl, isPdf])

  useLayoutEffect(() => {
    const preview = previewRef.current
    if (!preview) return

    preview.scrollLeft = Math.max(
      0,
      zoomFocusRef.current.x * preview.scrollWidth - preview.clientWidth / 2,
    )
    preview.scrollTop = Math.max(
      0,
      zoomFocusRef.current.y * preview.scrollHeight - preview.clientHeight / 2,
    )
  }, [zoomLevel])

  const changeZoom = (change: number): void => {
    const preview = previewRef.current
    if (preview) {
      zoomFocusRef.current = {
        x:
          (preview.scrollLeft + preview.clientWidth / 2) /
          Math.max(preview.scrollWidth, 1),
        y:
          (preview.scrollTop + preview.clientHeight / 2) /
          Math.max(preview.scrollHeight, 1),
      }
    }

    setZoomLevel((currentZoomLevel) =>
      Math.min(
        MAX_ZOOM_LEVEL,
        Math.max(MIN_ZOOM_LEVEL, currentZoomLevel + change),
      ),
    )
  }

  const startPan = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (zoomLevel === MIN_ZOOM_LEVEL || event.button !== 0) return

    panStateRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollLeft: event.currentTarget.scrollLeft,
      scrollTop: event.currentTarget.scrollTop,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    setIsPanning(true)
    event.preventDefault()
  }

  const pan = (event: React.PointerEvent<HTMLDivElement>): void => {
    const panState = panStateRef.current
    if (!panState || panState.pointerId !== event.pointerId) return

    event.currentTarget.scrollLeft =
      panState.scrollLeft - (event.clientX - panState.clientX)
    event.currentTarget.scrollTop =
      panState.scrollTop - (event.clientY - panState.clientY)
    event.preventDefault()
  }

  const stopPan = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (panStateRef.current?.pointerId !== event.pointerId) return

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    panStateRef.current = undefined
    setIsPanning(false)
  }

  const submitReview = (outcome: 'MATCHES' | 'DOES_NOT_MATCH'): void => {
    setPendingReviewOutcome(outcome)
    onSubmitReview?.(outcome)
  }

  const metadata = (
    <div className={'ReceiptEvidencePanel--metadata'}>
      {submittedAt ? (
        <span>
          {translate('Submitted on ${date}${by}', {
            date: submittedAt,
            by: submittedBy ? ` by ${submittedBy}` : '',
          })}
        </span>
      ) : null}
      {isInAppUpload && <span>{translate('· In-app upload')}</span>}
      {!isManualAttachment && isRecoveredFromGmail && (
        <span className={'ReceiptEvidencePanel--provenance'}>
          {translate('· Found through Gmail')}
          <img src={Gmail2026} alt={translate('Gmail')} />
        </span>
      )}
    </div>
  )

  return (
    <section
      css={style}
      className={'ReceiptEvidencePanel'}
      aria-label={translate('Receipt evidence')}
      data-testid={'receipt-evidence-panel'}
    >
      <div className={'ReceiptEvidencePanel--scroll-content'}>
        <div className={'ReceiptEvidencePanel--action-bar'}>
          <div className={'ReceiptEvidencePanel--action-group'}>
            {openUrl ? (
              <a
                className={'ReceiptEvidencePanel--icon-button'}
                href={openUrl}
                target={'_blank'}
                rel={'noopener noreferrer'}
                aria-label={translate('Open receipt in a new tab')}
                data-testid={'receipt-evidence-open'}
              >
                <ReceiptEvidenceActionIcon
                  name={'external-link'}
                  src={ReceiptEvidenceExternalLink}
                />
              </a>
            ) : (
              <button
                className={'ReceiptEvidencePanel--icon-button'}
                type={'button'}
                disabled
                aria-label={translate('Open receipt in a new tab')}
                data-testid={'receipt-evidence-open'}
              >
                <ReceiptEvidenceActionIcon
                  name={'external-link'}
                  src={ReceiptEvidenceExternalLink}
                />
              </button>
            )}
            <button
              className={'ReceiptEvidencePanel--icon-button'}
              type={'button'}
              disabled={!isDeleteEnabled}
              onClick={() => {
                if (isDeleteEnabled && attachmentId) {
                  onDelete?.(attachmentId)
                }
              }}
              aria-label={translate('Delete receipt')}
              data-testid={'receipt-evidence-delete'}
            >
              <ReceiptEvidenceActionIcon
                name={'delete'}
                src={ReceiptEvidenceDelete}
              />
            </button>
            {originalUrl ? (
              <a
                className={'ReceiptEvidencePanel--icon-button'}
                href={originalUrl}
                download={fileName}
                aria-label={translate('Download receipt')}
                data-testid={'receipt-evidence-download'}
              >
                <ReceiptEvidenceActionIcon name={'download'} />
              </a>
            ) : (
              <button
                className={'ReceiptEvidencePanel--icon-button'}
                type={'button'}
                disabled
                aria-label={translate('Download receipt')}
                data-testid={'receipt-evidence-download'}
              >
                <ReceiptEvidenceActionIcon name={'download'} />
              </button>
            )}
          </div>
          <div className={'ReceiptEvidencePanel--action-group'}>
            <button
              className={'ReceiptEvidencePanel--icon-button'}
              type={'button'}
              onClick={() => changeZoom(ZOOM_LEVEL_STEP)}
              disabled={!isPreviewImage || zoomLevel === MAX_ZOOM_LEVEL}
              aria-label={translate('Zoom receipt')}
            >
              <ReceiptEvidenceActionIcon
                name={'plus'}
                src={ReceiptEvidencePlus}
              />
            </button>
            <button
              className={'ReceiptEvidencePanel--icon-button'}
              type={'button'}
              onClick={() => changeZoom(-ZOOM_LEVEL_STEP)}
              disabled={!isPreviewImage || zoomLevel === MIN_ZOOM_LEVEL}
              aria-label={translate('Zoom receipt out')}
            >
              <ReceiptEvidenceActionIcon
                name={'collapse'}
                src={ReceiptEvidenceCollapse}
              />
            </button>
          </div>
        </div>

        <div
          ref={previewRef}
          className={'ReceiptEvidencePanel--preview'}
          role={'region'}
          aria-label={translate('Receipt: ${filename}', {
            filename: fileName,
          })}
          tabIndex={zoomLevel > MIN_ZOOM_LEVEL ? 0 : undefined}
          data-zoomed={zoomLevel > MIN_ZOOM_LEVEL}
          data-panning={isPanning}
          data-testid={'receipt-evidence-preview'}
          onPointerDown={startPan}
          onPointerMove={pan}
          onPointerUp={stopPan}
          onPointerCancel={stopPan}
        >
          {isPdf && pdfPreview === undefined ? (
            <div
              className={'ReceiptEvidencePanel--unavailable'}
              aria-busy={'true'}
            >
              <span>{translate('Loading')}</span>
            </div>
          ) : previewUrl ? (
            isPreviewImage ? (
              <div
                className={'ReceiptEvidencePanel--preview-canvas'}
                data-testid={'receipt-evidence-preview-canvas'}
                style={
                  {
                    '--receipt-evidence-zoom': zoomLevel,
                  } as React.CSSProperties
                }
              >
                <img
                  className={'ReceiptEvidencePanel--preview-image'}
                  src={previewUrl}
                  draggable={false}
                  alt={translate('Receipt: ${filename}', {
                    filename: fileName,
                  })}
                />
              </div>
            ) : isPreviewDocument ? (
              <object
                className={'ReceiptEvidencePanel--preview-document'}
                data={previewUrl}
                type={'application/pdf'}
                aria-label={translate('Receipt: ${filename}', {
                  filename: fileName,
                })}
                title={translate('Receipt: ${filename}', {
                  filename: fileName,
                })}
              />
            ) : (
              <iframe
                className={'ReceiptEvidencePanel--preview-document'}
                src={previewUrl}
                title={translate('Receipt: ${filename}', {
                  filename: fileName,
                })}
              />
            )
          ) : (
            <div className={'ReceiptEvidencePanel--unavailable'}>
              <AttachmentFileIcon width={24} height={24} aria-hidden={'true'} />
              <span>
                {translate(
                  'The recovered document is not available to preview.',
                )}
              </span>
            </div>
          )}
        </div>

        <div className={'ReceiptEvidencePanel--divider'} />

        {isReviewerDrivenReceipt ? (
          <div className={'ReceiptEvidencePanel--review-details'}>
            {isNeedsReview ? (
              <>
                <Pill size={'small'} color={'pending'}>
                  {translate('Needs Review')}
                </Pill>
                <div className={'ReceiptEvidencePanel--agent-provenance'}>
                  <ReceiptAgentAvatar
                    className={'ReceiptEvidencePanel--agent-avatar'}
                  />
                  <span className={'ReceiptEvidencePanel--agent-label'}>
                    {translate('Receipt Agent')}
                  </span>
                  {submittedAt && (
                    <span className={'ReceiptEvidencePanel--agent-meta'}>
                      {translate('· ${date}', { date: submittedAt })}
                    </span>
                  )}
                </div>
                <div className={'ReceiptEvidencePanel--recovered-title'}>
                  {translate('Recovered ${filename}', {
                    filename: fileName,
                  })}
                </div>
                <p className={'ReceiptEvidencePanel--review-description'}>
                  {translate(
                    'The Receipt Agent found a receipt that may match this transaction. Review the receipt and confirm if it’s a match.',
                  )}
                </p>
              </>
            ) : (
              <div
                className={`ReceiptEvidencePanel--review-outcome ReceiptEvidencePanel--review-outcome-${reviewerDecision?.toLowerCase()}`}
              >
                <span className={'ReceiptEvidencePanel--review-outcome-label'}>
                  {translate(
                    reviewerDecision === 'APPROVE'
                      ? 'Confirmed match'
                      : 'Does not match',
                  )}
                  {reviewerDecision === 'APPROVE' ? (
                    <CheckCircleFilledIcon aria-hidden={'true'} />
                  ) : (
                    <XCircleFilledIcon aria-hidden={'true'} />
                  )}
                </span>
                {reviewedBy && (
                  <span>{translate('· by ${name}', { name: reviewedBy })}</span>
                )}
              </div>
            )}
            <div className={'ReceiptEvidencePanel--divider'} />
            {metadata}
          </div>
        ) : (
          metadata
        )}
      </div>

      {isNeedsReview && (
        <footer
          className={'ReceiptEvidencePanel--review-footer'}
          data-testid={'receipt-evidence-review-footer'}
          aria-busy={isSubmittingReview}
        >
          {reviewError && (
            <div role={'alert'}>
              <AlertPanel variant={'severe-wcag'}>{reviewError}</AlertPanel>
            </div>
          )}
          <div className={'ReceiptEvidencePanel--review-actions'}>
            <Button
              className={'ReceiptEvidencePanel--match-button'}
              variant={'secondary'}
              dropShadow={false}
              iconLeft={<CheckmarkIcon aria-hidden={'true'} />}
              loading={isSubmittingReview && pendingReviewOutcome === 'MATCHES'}
              disabled={isSubmittingReview || onSubmitReview === undefined}
              onClick={() => submitReview('MATCHES')}
            >
              {translate('Receipt matches')}
            </Button>
            <Button
              className={'ReceiptEvidencePanel--reject-button'}
              variant={'severe-secondary'}
              dropShadow={false}
              iconLeft={
                <CloseIcon
                  width={16}
                  height={16}
                  aria-hidden={'true'}
                  data-testid={'receipt-evidence-reject-icon'}
                />
              }
              loading={
                isSubmittingReview && pendingReviewOutcome === 'DOES_NOT_MATCH'
              }
              disabled={isSubmittingReview || onSubmitReview === undefined}
              onClick={() => submitReview('DOES_NOT_MATCH')}
            >
              {translate('Receipt doesn’t match')}
            </Button>
          </div>
        </footer>
      )}
    </section>
  )
}

export default ReceiptEvidencePanel
